import { createContext, useContext } from "react";
import * as RoutesModule from "Routes";
import App from "./App";
import { createRoot } from "react-dom/client";

// Stores and Services
import { ConfigurationService } from "services/ConfigurationService";
import { ServiceTypes } from "./constants/ServiceTypes";
import { AxiosServiceFactory } from "services/AxiosServiceFactory";
import { AuthenticationService } from "services/AuthenticationService";
import { AxiosServiceConfig } from "services/AxiosServiceConfig";
import { AxiosErrorHandler } from "services/AxiosErrorHandler";
import { SystemApiService } from "services/data-services/SystemApiService";
import { NotificationsStore } from "stores/NotificationsStore";
import StartupService from "services/StartupService";
import StartupStore from "stores/StartupStore";
import { StatusStore } from "stores/StatusStore";
import { WomApiService } from "services/data-services/WomApiService";
import { SystemSignalRService } from "services/data-services/SystemSignalRService";
import { BrowserRouter } from "react-router-dom";
import { AxiosInstance } from "axios";
import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import translationsEn from "./locales/en/translation-en.json";
import registerServiceWorker from "./registerServiceWorker";
import { SystemStore } from "stores/SystemStore";

const rootElement = document.getElementById("root");
const root = createRoot(rootElement!);

registerServiceWorker();

type Stores = {
  errorHandler: AxiosErrorHandler;
  configService: ConfigurationService;
  authenticationService: AuthenticationService;
  apiServiceAxiosInstance: AxiosInstance;
  notificationStore: NotificationsStore;
  statusStore: StatusStore;
  systemApiService: SystemApiService;
  startupStore: StartupStore;
  systemStore: SystemStore;
};

const StoresContext = createContext<Stores | null>(null);

export const useStores = () => {
  const stores = useContext(StoresContext);
  if (!stores) {
    throw new Error("useStores must be used within a StoreProvider.");
  }
  return stores;
};

const langResources = {
  en: { translation: translationsEn },
};

export const isLangAvailable = (lang: string) => {
  return Object.keys(langResources).includes(lang) ? lang : "";
};

async function renderApp() {
  const errorHandler = new AxiosErrorHandler();
  const configurationService = new ConfigurationService();
  const authenticationService = new AuthenticationService();
  const serviceFactory = new AxiosServiceFactory(configurationService);
  const apiServiceAxiosInstance = await serviceFactory.createInstance(
    ServiceTypes.ApiService
  );
  const womApiServiceAxiosInstance = await serviceFactory.createInstance(
    ServiceTypes.WomApiService
  );
  const configs = await configurationService.getConfigs();

  const systemApiService = new SystemApiService(apiServiceAxiosInstance);
  const womApiService = new WomApiService(womApiServiceAxiosInstance);
  const systemSignalRService = new SystemSignalRService(
    configs.womApiUrl + "systemhub"
  );
  const notificationStore = new NotificationsStore();

  const startupService = new StartupService(apiServiceAxiosInstance);
  const startupStore = new StartupStore(
    authenticationService,
    configurationService,
    startupService,
    notificationStore
  );

  const systemStore = new SystemStore(
    notificationStore,
    systemSignalRService,
    configs.googleApiUrl,
    configs.tscUrl
  );

  const statusStore = new StatusStore(womApiService, notificationStore);

  const stores: Stores = {
    errorHandler,
    configService: configurationService,
    authenticationService,
    apiServiceAxiosInstance,
    notificationStore,
    systemApiService,
    statusStore,
    startupStore,
    systemStore,
  };

  await i18next.use(initReactI18next).init({
    resources: langResources,
    lng: isLangAvailable(navigator.language.slice(0, 2)) || "en",
    fallbackLng: "en",
    interpolation: { escapeValue: false },
  });

  // Add a request and response interceptor
  AxiosServiceConfig.setDefaultDecorators(
    apiServiceAxiosInstance,
    authenticationService,
    errorHandler
  );
  AxiosServiceConfig.setDefaultDecorators(
    womApiServiceAxiosInstance,
    authenticationService,
    errorHandler
  );

  root.render(
    <BrowserRouter>
      <StoresContext.Provider value={stores}>
        <App>{RoutesModule.routes}</App>
      </StoresContext.Provider>
    </BrowserRouter>
  );
}

renderApp();
